import { Injectable, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ProductClassifications } from '../enums/product-classifications';
import { RemoveProductDialogComponent } from '@shared/components/remove-product-dialog/remove-product-dialog.component';
import { dialogWidth, dialogWidthSmall, dialogWidthLarge } from '../constants/themes';
import { AccessorySearchComponent } from '@sales/modules/accessories/components/accessories/accessory-search/accessory-search.component';
import { NewSaleDialogComponent } from '@sales/components/new-sale-dialog/new-sale-dialog.component';
import { MobilephoneSearchComponent } from '@sales/modules/mobilephone/components/mobilephone-search/mobilephone-search.component';
import { SecretNumberMessageComponent } from '@sales/modules/details/components/secret-number-message/secret-number-message.component';
import { MaximumNumberOfSubscriptionsDialogComponent } from '@sales/components/maximum-number-of-subscriptions-dialog/maximum-number-of-subscriptions-dialog.component';
import { VerifyExistingMobileComponent } from '@sales/modules/mobilephone/components/mobilephonesales/change-mobile/verify-existing-mobile/verify-existing-mobile.component';
import { MobilephoneMobilbytteBytteSearchComponent } from '@sales/modules/mobilephone/components/mobilephone-mobilbytte-bytte-search/mobilephone-mobilbytte-bytte-search.component';
import { MobilePhoneProduct } from '@sales/models/mobilephone-product';
import { CreateSellerDialogComponent } from '@support/components/create-seller-dialog/create-seller-dialog.component';
import { CancelOrderDialogComponent } from '@support/components/cancel-order-dialog/cancel-order-dialog.component';
import { FeedbackDialogComponent } from '../components/main-menu/feedback-dialog/feedback-dialog.component';
import { ChangeDealerDialogComponent } from '../components/main-menu/change-dealer-dialog/change-dealer-dialog.component';
import { Dealer, Seller } from '../models/dealer.model';
import { User } from '@core/models/user.model';
import { RemoveSellerDialogComponent } from '@support/components/sellers/remove-seller-dialog/remove-seller-dialog.component';
import { GtinInfoDialogComponent } from '@sales/components/gtin-info-dialog/gtin-info-dialog.component';
import { ImeiInfoDialogComponent } from '@sales/components/imei-info-dialog/imei-info-dialog.component';
import { CancelRegisterCustomerDialogComponent } from '@app/sales/components/content-wrapper/cancel-register-customer-dialog/cancel-register-customer-dialog.component';
import { DealerCompetitionDialogComponent } from '@app/support/modules/competition/components/dealer-competition-card/dealer-competition-dialog/dealer-competition-dialog.component';
import { DealerCompetitionActionType } from '@app/support/modules/competition/enums/dealer-competition-action-type.enum';
import { Competition } from '@app/support/models/competition.model';
import { TradeInDialogComponent } from '@app/sales/modules/mobilephone/components/trade-in-dialog/trade-in-dialog.component';
import { ReportErrorDialogComponent } from '../components/main-menu/report-error-dialog/report-error-dialog.component';
import { HardwareVariant } from '@app/sales/modules/mobilephone/models/mobilephone-variant';
import { EquipmentDialogComponent } from '@app/sales/modules/equipment/equipment-dialog/equipment-dialog.component';
import { EquipmentVariant } from '@app/sales/modules/equipment/equipment.models';
import { CustomMatDialogService } from './mat-dialog.service';
import { RegisterMobilbytteDialogComponent } from '@app/sales/modules/mobilephone/components/register-mobilbytte-dialog/register-mobilbytte-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { BypassOrderModal } from '@app/support/modules/order-details/components/order-details-status/bypass-order-modal/bypass-order-modal.component';
import { NewBankidVerificationModal } from '@app/support/modules/order-details/components/order-details-status/new-bankid-verification-modal/new-bankid-verification-modal.component';
import { TradeInResetConfirmDialogComponent } from '@app/sales/modules/mobilephone/components/trade-in-reset-confirm-dialog/trade-in-reset-confirm-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class OpenDialogService {
  private dialog = inject(CustomMatDialogService);

  openEquipmentDialog(equipmentVariant: EquipmentVariant): MatDialogRef<EquipmentDialogComponent> {
    return this.dialog.open(EquipmentDialogComponent, {
      panelClass: ['ice-custom-panel', 'full-cover-panel'],
      disableClose: true,
      data: {
        selectedEquipmentVariant: equipmentVariant,
      },
    });
  }

  openRemoveProductDialog(product, classification: ProductClassifications, disableClose: boolean = false) {
    this.dialog.open(RemoveProductDialogComponent, {
      width: dialogWidth,
      data: {
        product: product,
        productClassifications: classification,
      },
      panelClass: 'ice-custom-panel',
      hasBackdrop: true,
      disableClose: disableClose,
    });
  }

  openCancelSaleDialog() {
    this.dialog.open(NewSaleDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: true,
    });
  }

  openCancelRegisterCustomerDialog(navigationPath: string) {
    this.dialog.open(CancelRegisterCustomerDialogComponent, {
      data: {
        navigationPath,
      },
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: true,
    });
  }

  openSecretNumberDialog() {
    this.dialog.open(SecretNumberMessageComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
    });
  }

  openMaximumNumberOfSubscriptionsDialog() {
    this.dialog.open(MaximumNumberOfSubscriptionsDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
    });
  }

  openAccessorySearchDialog(
    cartHasPcmProduct: boolean = false,
    cartHasPimProduct: boolean = false
  ): MatDialogRef<AccessorySearchComponent> {
    return this.dialog.open(AccessorySearchComponent, {
      data: {
        cartHasPimProduct,
        cartHasPcmProduct,
      },
      panelClass: ['ice-custom-panel', 'full-cover-panel'],
      disableClose: true,
    });
  }

  openMobilePhoneSearchDialog(
    isManualRegistration: boolean,
    mobileProductInCart?: MobilePhoneProduct,
    mobilePhoneVariants?: HardwareVariant[]
  ) {
    return this.dialog.open(MobilephoneSearchComponent, {
      data: {
        isManualRegistration,
        mobileProductInCart,
        mobilePhoneVariants,
      },
      panelClass: ['ice-custom-panel', 'full-cover-panel'],
      disableClose: true,
      autoFocus: false,
    });
  }

  openMobilephoneMobilbytteBytteSearchDialog(
    equipmentUpgradeAgreementId: number,
    isManualRegistration: boolean,
    route: ActivatedRoute
  ) {
    this.closeAllDialog();
    return this.dialog.open(MobilephoneMobilbytteBytteSearchComponent, {
      data: {
        equipmentUpgradeAgreementId,
        isManualRegistration,
        route,
      },

      panelClass: ['ice-custom-panel', 'full-cover-panel'],
      disableClose: true,
    });
  }

  openRegisterMobilbytteDialog(equipmentUpgradeAgreementId: number, route: ActivatedRoute) {
    this.closeAllDialog();
    return this.dialog.open(RegisterMobilbytteDialogComponent, {
      data: {
        equipmentUpgradeAgreementId,
        route,
      },
      panelClass: ['ice-custom-panel', 'full-cover-panel'],
      disableClose: true,
    });
  }

  openMobilbytteBytteMobilePhoneDialog(existingImei: string) {
    return this.dialog.open(VerifyExistingMobileComponent, {
      maxWidth: '41rem',
      width: '41rem',
      panelClass: 'ice-custom-panel',
      data: existingImei,
    });
  }

  openTradeInDialog() {
    return this.dialog.open(TradeInDialogComponent, {
      panelClass: ['ice-custom-panel', 'full-cover-panel'],
      disableClose: true,
      autoFocus: false,
    });
  }

  openTradeInResetConfirmDialog(): MatDialogRef<TradeInResetConfirmDialogComponent, boolean> {
    return this.dialog.open(TradeInResetConfirmDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: true,
    });
  }

  openCreateSellerDialog() {
    this.dialog.open(CreateSellerDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: false,
    });
  }

  openCancelOrderDialog(orderServiceId: number) {
    this.dialog.open(CancelOrderDialogComponent, {
      width: dialogWidth,
      data: {
        orderServiceId,
      },
      panelClass: 'ice-custom-panel',
      disableClose: false,
    });
  }

  openFeedbackDialog(activeDealer: Dealer, user: User) {
    return this.dialog.open(FeedbackDialogComponent, {
      width: dialogWidthSmall,
      panelClass: 'ice-custom-panel',
      disableClose: false,
      data: { activeDealer, user },
    });
  }

  openChangeDealerDialog(activeDealer: Dealer, dealers: Dealer[]) {
    this.dialog.open(ChangeDealerDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: false,
      data: { activeDealer, dealers },
    });
  }

  openRemoveSellerFromDealerDialog(seller: Seller) {
    this.dialog.open(RemoveSellerDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: false,
      data: { seller },
    });
  }

  openGtinInfoDialog() {
    this.dialog.open(GtinInfoDialogComponent, {
      width: dialogWidthLarge,
      panelClass: 'ice-custom-panel',
      disableClose: false,
    });
  }

  openImeiInfoDialog() {
    this.dialog.open(ImeiInfoDialogComponent, {
      width: dialogWidthLarge,
      panelClass: 'ice-custom-panel',
      disableClose: false,
    });
  }

  openCompetitionMenuDialog(dealerCompetitionAction: DealerCompetitionActionType, dealerCompetition?: Competition) {
    return this.dialog.open(DealerCompetitionDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: false,
      data: { dealerCompetitionAction, dealerCompetition },
      hasBackdrop: true,
    });
  }

  openErrorDialog(user: User) {
    return this.dialog.open(ReportErrorDialogComponent, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      hasBackdrop: true,
      maxHeight: '90vh',
      data: { user },
    });
  }

  openBypassOrderDialog(orderServiceId: number) {
    return this.dialog.open(BypassOrderModal, {
      width: dialogWidth,
      panelClass: 'ice-custom-panel',
      disableClose: true,
      autoFocus: false,
      maxHeight: '90vh',
      data: { orderServiceId },
    });
  }

  openNewBankIdVerificationDialog(orderServiceId: number, initialPhoneNumber?: string | null, initialEmail?: string | null) {
    return this.dialog.open(NewBankidVerificationModal, {
      width: dialogWidthLarge,
      panelClass: 'ice-custom-panel',
      disableClose: true,
      autoFocus: false,
      maxHeight: '90vh',
      data: { orderServiceId, initialPhoneNumber, initialEmail },
    });
  }

  isDialogOpen(): boolean {
    return this.dialog.openDialogs.length > 0;
  }

  private closeAllDialog() {
    this.dialog.closeAll();
  }
}
